import React, {
    useCallback, useEffect, useMemo, useState
} from 'react'
import { filter, minBy } from 'lodash'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { DropdownButton } from 'components'
import { colors, _ } from 'utils'
import Icon from 'components/Icon'
import CategoryList from 'components/lists/CategoryList'
import { useEventSlug, useQuery } from 'hooks'
import {
    CreateTag, CreateTagCategory, ProgramTagsQueryVariables
} from 'generated/graphql'
import CategoryModal from 'components/CategoryModal'
import TagModal from 'components/TagModal'
import { getCategoryFrom, TagCategoriesQuery, TagsQuery } from 'utils/categoryTag'
import { DocumentNode } from 'graphql'
import { addModal } from '../modals/modalSlice'

const S = {
    Wrapper: styled.div`
        width: 100%;
        padding: 0px 10px 0px 10px;
    `,
    Header: styled.div`
        margin: -38px -10px 20px -10px;
        padding: 0 20px;
        width: calc(100% + 20px);
        height: 56px;
        display: flex;
        align-items: center;
        font-size: 14px;
        background-color: rgba(48, 104, 244, 0.03);
        justify-content: space-between;
    `,
    Icon: styled(Icon)`
        margin: 0 10px 3px 0;
    `,
}

interface Props {
    description: string
    tagKey: 'programTags' | 'userTags' | 'movieTags'
    categoryKey: 'programTagCategories' | 'attendeeTagCategories' | 'movieTagCategories'
    tagsQuery: DocumentNode
    categoriesQuery: DocumentNode
    updateTagOrder: (id, order, categoryId?) => Promise<any>
    updateCategoryOrder: (id, order) => Promise<any>
    deleteCategory: (id) => Promise<any>
    deleteTag: (id) => Promise<any>
    updateTag:({ text, color, categoryId }, id) => Promise<any>
    createTag:(data: CreateTag) => Promise<any>
    createCategory: (data: CreateTagCategory) => Promise<any>
    updateCategory: (id, text) => Promise<any>

}

const TagsPage = ({
    tagsQuery, categoriesQuery, updateCategoryOrder, updateTagOrder,
    deleteTag, updateTag, deleteCategory, createTag, tagKey, categoryKey,
    createCategory, updateCategory, description
}: Props) => {
    const eventSlug = useEventSlug()
    const dispatch = useDispatch()
    const [minCategoryOrder, setMinCategoryOrder] = useState<number>()
    const [minTagOrder, setMinTagOrder] = useState<number>()
    const { data: tagsData, fetchData } = useQuery<
        TagsQuery, ProgramTagsQueryVariables
        >(tagsQuery, {
            eventId: eventSlug,
        })

    const { data: categoriesData, fetchData: fetchCategories } = useQuery<
        TagCategoriesQuery, ProgramTagsQueryVariables
        >(categoriesQuery, {
            eventId: eventSlug,
        })

    const categories = useMemo(() => getCategoryFrom(tagsData, categoriesData, tagKey, categoryKey),
        [tagsData, categoriesData, tagKey, categoryKey])
    const unassignedTags = useMemo(() => filter(tagsData?.event?.[tagKey], (tag) => !tag.category),
        [tagsData, tagKey])

    useEffect(() => {
        const category = minBy(categories, 'order')
        setMinCategoryOrder(category ? category.order : 0)
    }, [categories])

    useEffect(() => {
        const tag = minBy(unassignedTags, 'order')
        setMinTagOrder(tag ? tag.order : 0)
    }, [unassignedTags])

    const showCategoryModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CategoryModal
                    createCategory={createCategory}
                    updateCategory={updateCategory}
                    close={close}
                    onSubmitted={fetchCategories}
                    minOrder={minCategoryOrder}
                />
            ),
            size: 'xl',
            shouldNotWrap: true
        }))
    }, [createCategory, dispatch, fetchCategories, minCategoryOrder, updateCategory])

    const showTagModal = useCallback(() => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <TagModal
                    close={close}
                    onSubmitted={fetchData}
                    minOrder={minTagOrder}
                    createTag={createTag}
                />
            ),
            size: 'xl',
            shouldNotWrap: true
        }))
    }, [dispatch, fetchData, minTagOrder, createTag])
    return (
        <S.Wrapper>
            <S.Header>
                <div>
                    <S.Icon
                        icon="info"
                        color={colors.primary}
                        size={20}
                    />
                    <span>{description}</span>
                </div>
                <DropdownButton
                    name={_('button.add')}
                    variant="primary"
                    icon="plusBox"
                    className="ml-2"
                >
                    <div onClick={showCategoryModal}>Category</div>
                    <div onClick={showTagModal}>Tag</div>
                </DropdownButton>
            </S.Header>
            <CategoryList
                categories={categories}
                unassignedTags={unassignedTags}
                updateMinCategoryOrder={setMinCategoryOrder}
                updateMinTagOrder={setMinTagOrder}
                updateTagOrder={updateTagOrder}
                updateCategoryOrder={updateCategoryOrder}
                fetchTags={fetchData}
                fetchCategories={fetchCategories}
                deleteCategory={deleteCategory}
                deleteTag={deleteTag}
                updateTag={updateTag}
                updateCategory={updateCategory}
                createTag={createTag}
            />
        </S.Wrapper>
    )
}

export default TagsPage
